import axios from "axios";
import { getErrorMessage, handleApiError } from "../utils/errorHandler";
import API_BASE_URL from "../config/api";

// Base URL for your Flask backend
const BASE_URL = API_BASE_URL;
console.log('🔧 Bulk Density API - Base URL:', BASE_URL);

// Get all bulk density test requests (for dashboard)
export const getAllBulkDensityTestRequests = async () => {
  const url = `${BASE_URL}/bulk-density/test-requests`;
  try {
    console.log('🔵 [BULK DENSITY API] Fetching all test requests for dashboard');
    console.log('🌐 [BULK DENSITY API] Full URL:', url);
    const response = await axios.get(url);
    console.log('✅ [BULK DENSITY API] All test requests fetched successfully:', response.data);
    return response.data.test_requests || response.data || [];
  } catch (error) {
    const errorStatus = error.response?.status;
    const errorMessage = error.message || 'Unknown error';
    const errorData = error.response?.data;
    
    console.error("❌ [BULK DENSITY API] Error fetching all test requests");
    console.error("   Status:", errorStatus);
    console.error("   Message:", errorMessage);
    console.error("   URL:", url);
    console.error("   Response data:", errorData);
    
    if (errorStatus === 404) {
      console.warn("⚠️ [BULK DENSITY API] Endpoint not found (404). The backend route may not be deployed.");
      console.warn("   Expected endpoint: GET /api/bulk-density/test-requests");
      console.warn("   Please ensure the backend is deployed with the latest code");
    }
    
    // Return empty array on error to not break dashboard
    return [];
  }
};

// Get a single bulk density test request by ID
export const getBulkDensityTestRequest = async (testRequestId) => {
  const url = `${BASE_URL}/bulk-density/test-requests/${testRequestId}`;
  try {
    console.log('🔵 [BULK DENSITY API] Fetching test request ID:', testRequestId);
    console.log('🌐 [BULK DENSITY API] Full URL:', url);
    const response = await axios.get(url);
    console.log('✅ [BULK DENSITY API] Test request fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ [BULK DENSITY API] Error fetching test request");
    console.error("   Status:", error.response?.status);
    console.error("   Message:", error.message);
    console.error("   URL:", url);
    handleApiError(error, "Failed to fetch Bulk Density test request. Please try again.");
    throw error;
  }
};

// Create a new Bulk Density test request
export const createBulkDensityTestRequest = async (testRequestData) => {
  const url = `${BASE_URL}/bulk-density/test-requests`;
  console.log('🌐 API Call - Creating Bulk Density test request');
  console.log('📍 URL:', url);
  console.log('📦 Data:', testRequestData);
  
  try {
    const response = await axios.post(url, testRequestData);
    console.log('✅ Success:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error creating Bulk Density test request:", error);
    console.error("❌ Error details:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: url
    });
    const errorMessage = getErrorMessage(error);
    handleApiError(error, "Failed to create Bulk Density test request. Please try again.");
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

// Update a bulk density test request
export const updateBulkDensityTestRequest = async (testRequestId, testRequestData) => {
  const url = `${BASE_URL}/bulk-density/test-requests/${testRequestId}`;
  try {
    console.log('🔵 [BULK DENSITY API] Updating test request ID:', testRequestId);
    console.log('🌐 [BULK DENSITY API] Full URL:', url);
    console.log('📦 Data:', testRequestData);
    const response = await axios.put(url, testRequestData);
    console.log('✅ [BULK DENSITY API] Test request updated successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ [BULK DENSITY API] Error updating test request");
    console.error("   Status:", error.response?.status);
    console.error("   Message:", error.message);
    console.error("   URL:", url);
    const errorMessage = getErrorMessage(error);
    handleApiError(error, "Failed to update Bulk Density test request. Please try again.");
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    throw enhancedError;
  }
};

// Delete a bulk density test request
export const deleteBulkDensityTestRequest = async (testRequestId) => {
  const url = `${BASE_URL}/bulk-density/test-requests/${testRequestId}`;
  try {
    console.log('🔵 [BULK DENSITY API] Deleting test request ID:', testRequestId);
    console.log('🌐 [BULK DENSITY API] Full URL:', url);
    const response = await axios.delete(url);
    console.log('✅ [BULK DENSITY API] Test request deleted successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error("❌ [BULK DENSITY API] Error deleting test request");
    console.error("   Status:", error.response?.status);
    console.error("   Message:", error.message);
    console.error("   URL:", url);
    handleApiError(error, "Failed to delete Bulk Density test request. Please try again.");
    throw error;
  }
};
